import { MenuItem, Skeleton, Stack } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useField } from "formik";
import { useSnackbar } from "notistack";
import { Fragment } from "react";
import { getBankAccounts } from "Services/api/profile/profile";
import { getAccountTypeChoices, getBankChoices } from "Services/api/request/request";
import ClientIdInput from "Shared/ClientIdInput/ClientIdInput";
import FormikSelect from "Shared/FormikSelect/FormikSelect";
import FormikTextField from "Shared/FormikTextField/FormikTextField";
import { Query } from "Shared/Query/Query";
import { clientIdValidation, clientRncValidation, emailValidation, requiredValidation } from "Utils/validations";
import * as Yup from "yup";

export function BankAccountFields({
  accountField,
  label,
  showAccountField = true,
  extended = false,
}: {
  accountField: string;
  label: string;
  showAccountField?: boolean;
  extended?: boolean;
}) {
  return (
    <>
      {showAccountField && <BankAccountSelect accountField={accountField} label={label} />}
      <NewBankAccountFields accountField={accountField} extended={extended} />
    </>
  );
}

function BankAccountSelect({ accountField, label }: { accountField: string; label: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const result = useQuery({
    queryKey: [getBankAccounts.name],
    queryFn: async () => {
      try {
        const response = await getBankAccounts();
        return response.data;
      } catch (error) {
        enqueueSnackbar("Error obteniendo cuentas de banco del cliente", { variant: "error" });
        console.error(error);
        throw error;
      }
    },
  });

  return (
    <Query
      result={result}
      OnLoading={() => (
        <Stack spacing={1} width="100%">
          <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
          <Skeleton variant="rectangular" width={"100%"} height={56} />
        </Stack>
      )}
      onError={() => (
        <FormikSelect
          id={accountField}
          name={accountField}
          label={label}
          SelectDisplayProps={{ "aria-label": label }}
          required
        >
          <MenuItem value="new">Agregar cuenta</MenuItem>
        </FormikSelect>
      )}
      onSuccess={(bankAccounts) => (
        <FormikSelect
          id={accountField}
          name={accountField}
          label={label}
          SelectDisplayProps={{ "aria-label": label }}
          required
        >
          <MenuItem value="new">Agregar cuenta</MenuItem>
          {bankAccounts.map(({ id, number, bank }) => {
            return (
              <MenuItem key={id} value={id}>
                - {number} - {bank}
              </MenuItem>
            );
          })}
        </FormikSelect>
      )}
    />
  );
}

type AccountIdType = "Cedula" | "RNC";

export function newAccountFieldsInitialValues(extended = false) {
  const initialValues = {
    accountNumber: "",
    accountBank: "",
    accountType: "",
  };

  return extended
    ? { ...initialValues, accountIdType: "Cedula", accountId: "", accountName: "", accountEmail: "" }
    : initialValues;
}

export function NewBankAccountFields({ accountField, extended = false }: { accountField: string; extended?: boolean }) {
  const [{ value: account }] = useField<string>(accountField);
  const [{ value: accountIdType }] = useField<AccountIdType | undefined>("accountIdType");

  if (account !== "new") return null;

  let idLabel = "Cédula del beneficiario";
  let idPlaceholder = "000-0000000-0";

  if (accountIdType === "RNC") {
    idLabel = "RNC del beneficiario";
    idPlaceholder = "000-00000-0";
  }

  return (
    <>
      <BankAccountField />
      <AccountTypeField />
      <FormikTextField id="accountNumber" name="accountNumber" label="Numero de cuenta" variant="outlined" required />
      {extended && (
        <>
          <FormikSelect
            id="accountIdType"
            name="accountIdType"
            label="Tipo de identificación del beneficiario"
            SelectDisplayProps={{ "aria-label": "Tipo de identificación de beneficiario" }}
            required
          >
            <MenuItem value={"Cedula"}>Cédula</MenuItem>
            <MenuItem value={"RNC"}>RNC</MenuItem>
          </FormikSelect>
          <ClientIdInput name="accountId" id="accountId" label={idLabel} placeholder={idPlaceholder} />
          <FormikTextField
            id="accountName"
            name="accountName"
            label="Nombre del beneficiario"
            variant="outlined"
            required
          />
          <FormikTextField
            name="accountEmail"
            id="accountEmail"
            type="email"
            fullWidth
            label="Correo electrónico del beneficiario:"
            variant="outlined"
            required
          />
        </>
      )}
    </>
  );
}

function BankAccountField() {
  const { enqueueSnackbar } = useSnackbar();
  const getBanks = useQuery({
    queryKey: [getBankChoices.name],
    queryFn: async () => {
      try {
        const response = await getBankChoices();
        return response.data;
      } catch (error) {
        enqueueSnackbar("Error obteniendo instituciones financieras", { variant: "error" });
        console.error(error);
        throw error;
      }
    },
  });

  return (
    <Query
      result={getBanks}
      OnLoading={() => (
        <Stack spacing={1} width="100%">
          <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
          <Skeleton variant="rectangular" width={"100%"} height={56} />
        </Stack>
      )}
      onError={() => (
        <FormikSelect
          id="accountBank"
          name="accountBank"
          label="Institución financiera"
          SelectDisplayProps={{ "aria-label": "Institución financiera" }}
          required
        ></FormikSelect>
      )}
      onSuccess={(banks) => (
        <FormikSelect
          id="accountBank"
          name="accountBank"
          label="Institución financiera"
          SelectDisplayProps={{ "aria-label": "Institución financiera" }}
          required
        >
          {banks.map(([value, display]) => (
            <MenuItem key={value} value={value}>
              {display}
            </MenuItem>
          ))}
        </FormikSelect>
      )}
    />
  );
}

function AccountTypeField() {
  const { enqueueSnackbar } = useSnackbar();

  const getAccountTypes = useQuery({
    queryKey: [getAccountTypeChoices.name],
    queryFn: async () => {
      try {
        const response = await getAccountTypeChoices();
        return response.data;
      } catch (error) {
        enqueueSnackbar("Error obteniendo los tipos de cuentas", { variant: "error" });
        console.error(error);
        throw error;
      }
    },
  });

  return (
    <Query
      result={getAccountTypes}
      OnLoading={() => (
        <Stack spacing={1} width="100%">
          <Skeleton variant="text" sx={{ fontSize: "14px" }} width={120} />
          <Skeleton variant="rectangular" width={"100%"} height={56} />
        </Stack>
      )}
      onError={() => (
        <FormikSelect
          id="accountType"
          name="accountType"
          label="Tipo de cuenta"
          SelectDisplayProps={{ "aria-label": "Tipo de cuenta" }}
          required
        ></FormikSelect>
      )}
      onSuccess={(accountTypes) => (
        <FormikSelect
          id="accountType"
          name="accountType"
          label="Tipo de cuenta"
          SelectDisplayProps={{ "aria-label": "Tipo de cuenta" }}
          required
        >
          {accountTypes.map(([value, display]) => (
            <MenuItem key={value} value={value}>
              {display}
            </MenuItem>
          ))}
        </FormikSelect>
      )}
    />
  );
}

export function BankAccountFieldsSchema(accountField: string, extended = false) {
  let schema: { [key: string]: Yup.AnySchema } = {
    accountNumber: Yup.string().when(accountField, {
      is: "new",
      then: () => requiredValidation,
    }),
    accountBank: Yup.string().when(accountField, {
      is: "new",
      then: () => requiredValidation,
    }),
    accountType: Yup.string().when(accountField, {
      is: "new",
      then: () => requiredValidation,
    }),
  };

  if (extended) {
    schema = {
      ...schema,
      accountIdType: Yup.string().when(accountField, {
        is: "new",
        then: () => requiredValidation,
      }),
      accountId: Yup.string().when(accountField, {
        is: "new",
        then: (schema) =>
          schema.when("accountIdType", {
            is: "RNC",
            then: () => clientRncValidation,
            otherwise: () => clientIdValidation,
          }),
      }),
      accountName: Yup.string().when(accountField, {
        is: "new",
        then: () => requiredValidation,
      }),
      accountEmail: Yup.string().when(accountField, {
        is: "new",
        then: () => emailValidation,
      }),
    };
  }

  return Yup.object(schema);
}
